import { TypographyCaption } from '@heidi/design-system/typography';
import { cn } from '@/lib/cn';
import { useTranslations, type TranslationKey } from '@heidi/runtime/i18n';
import { useEffect, useRef, useState } from 'react';
import { ChatLoadingIndicator } from './ChatLoadingIndicator';
import { useContextualThinkingPhrases } from './useContextualThinkingPhrases';

/** Duration for the loader fade-out before showing completion text (ms) */
const FADE_OUT_MS = 200;

interface ThinkingBlockProps {
  status: 'thinking' | 'streaming' | 'complete';
  durationMs?: number;
  className?: string;
  /** User query for this turn; seeds the contextual loader narration */
  relatedUserQuery?: string;
  /** Accepted and forwarded by ChatTimeline; currently unread by this block */
  onSelectFollowUp?: (query: string) => void;
  /** Accepted and forwarded by ChatTimeline; currently unread by this block */
  onAbortStream?: () => void;
}

/**
 * Format duration in human-readable format
 */
const formatDuration = (ms: number): string => {
  const seconds = Math.floor(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const mins = Math.floor(seconds / 60);
  return `${mins}m ${seconds % 60}s`;
};

/**
 * ThinkingBlock - Extended thinking display for Claude 4 models (EVI-334, EVI-375)
 *
 * During thinking/streaming: Shows ChatLoadingIndicator with animated messages
 * On complete: Shows "Thought for Xs" text
 *
 * Uses a stable outer container with min-height so the transition from the
 * loading indicator to the completion text doesn't cause a layout jump.
 * The loader fades out (200ms opacity transition), then the completion text
 * fades in (animate-in).
 *
 * Reasoning text is intentionally hidden to avoid exposing system prompt details.
 */
export const ThinkingBlock = ({
  status,
  durationMs,
  className,
  relatedUserQuery = '',
}: ThinkingBlockProps): JSX.Element => {
  const { t } = useTranslations();

  // Chat is the pilot surface for context-aware loader narration. Falls back to
  // the generic phrases until the backend seam returns query-specific ones.
  const { phrases: contextualPhrases } = useContextualThinkingPhrases(relatedUserQuery);

  // Whether the ChatLoadingIndicator is still mounted (kept alive during fade-out)
  const [showLoader, setShowLoader] = useState(status !== 'complete');
  // Whether the loader is fading to opacity 0
  const [loaderFading, setLoaderFading] = useState(false);
  const fadeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (status === 'complete' && showLoader) {
      setLoaderFading(true);
      fadeTimerRef.current = setTimeout(() => {
        setShowLoader(false);
        fadeTimerRef.current = null;
      }, FADE_OUT_MS);
    }

    return () => {
      if (fadeTimerRef.current) {
        clearTimeout(fadeTimerRef.current);
        fadeTimerRef.current = null;
      }
    };
  }, [status, showLoader]);

  const isComplete = status === 'complete' && !showLoader;

  const showDuration = durationMs && durationMs >= 1000;
  const headerText =
    showDuration && durationMs
      ? t('chat.thoughtForDuration' as TranslationKey, {
          duration: formatDuration(durationMs),
        })
      : t('chat.thought' as TranslationKey);

  return (
    <div data-testid="thinking-block" className={cn('min-h-[36px]', className)}>
      {showLoader ? (
        <div
          className={cn(
            'transition-opacity ease-out',
            loaderFading && 'opacity-0'
          )}
          style={{ transitionDuration: `${FADE_OUT_MS}ms` }}
        >
          <ChatLoadingIndicator messages={contextualPhrases} />
        </div>
      ) : (
        <div
          className={cn(
            'flex min-h-[36px] flex-wrap items-center justify-between gap-x-2 gap-y-1',
            isComplete && 'animate-in fade-in duration-200'
          )}
        >
          <TypographyCaption className="text-text-tertiary">{headerText}</TypographyCaption>
        </div>
      )}
    </div>
  );
};
